import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Icon, Text, VStack, createListCollection } from '@chakra-ui/react';
import { Translate } from 'iconoir-react';
import type { PayloadLocale } from '@fluenceur/types';
import { Switch } from '@/components/ui/switch';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';

interface MessageLanguageToggleProps {
  enabled: boolean;
  value: PayloadLocale;
  onToggle: (enabled: boolean) => void;
  onChange: (language: PayloadLocale) => void;
  disabled?: boolean;
}

const MessageLanguageToggleComponent = ({
  enabled,
  value,
  onToggle,
  onChange,
  disabled,
}: MessageLanguageToggleProps) => {
  const { t } = useTranslation();

  const languageCollection = useMemo(
    () =>
      createListCollection<{ value: PayloadLocale; label: string }>({
        items: [
          { value: 'en', label: t('common.languages.english') },
          { value: 'fr', label: t('common.languages.french') },
          { value: 'it', label: t('common.languages.italian') },
        ],
      }),
    [t]
  );

  return (
    <Box
      bg="transparent"
      border="1px solid"
      borderColor={enabled ? 'primary.200' : 'gray.200'}
      rounded="xl"
      px={4}
      py={3}
    >
      <VStack align="stretch" gap={enabled ? 3 : 0}>
        <HStack justify="space-between" align="center">
          <HStack gap={3} align="start">
            <Icon asChild mt={0.5}>
              <Translate width={18} height={18} />
            </Icon>
            <VStack align="start" gap={0.5}>
              <Text fontSize="sm" fontWeight="medium">
                {t('invitations.modal.changeLanguage.label')}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {t('invitations.modal.changeLanguage.helper')}
              </Text>
            </VStack>
          </HStack>
          <Switch
            size="sm"
            colorPalette="primary"
            checked={enabled}
            onCheckedChange={({ checked }) => onToggle(checked)}
            disabled={disabled}
            aria-label={t('invitations.modal.changeLanguage.label')}
          />
        </HStack>

        {enabled && (
          <Box pl={9} maxW="240px">
            <SelectRoot
              collection={languageCollection}
              value={[value]}
              onValueChange={(e) => onChange((e.value[0] as PayloadLocale | undefined) ?? 'en')}
              size="sm"
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValueText placeholder={t('invitations.modal.changeLanguage.placeholder')} />
              </SelectTrigger>
              {/* Rendered in-place, not portaled: this toggle lives inside modal
                  dialogs, whose positioner pins at z-index 10000 — a body-portaled
                  select content opens behind it. Mirrors Chakra's "select within
                  dialog" guidance (same fix as TemplatePicker). */}
              <SelectContent portalled={false}>
                {languageCollection.items.map((item) => (
                  <SelectItem key={item.value} item={item}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export const MessageLanguageToggle = memo(MessageLanguageToggleComponent);
